import { GalleryField } from '@/components/form/fields/image/GalleryInput';
import { Property, SelectField } from '@/components/form/fields/select/SelectInput';
import { RichTextField } from '@/components/form/fields/text/RichTextInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { MultiTextAndIconField } from '@/components/form/fields/text/multi/MultiTextAndIconInput';
import { SpecIcons } from '@/components/icons/SpecIcons';
import { useCategories, useManufacturers } from '@/services/api/hooks/catalog';
import { ToggleField } from '@/components/form/fields/toggle/Toggle';
import { CheckboxField } from '@/components/form/fields/checkbox/Checkbox';
import { Counter } from '@/components/form/fields/number/Counter';
import { useGetRateCardTriggers } from '@/services/api/hooks/catalog';
import { useProducts } from '@/services/api/hooks/product';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { Controller, useFormContext } from 'react-hook-form';
import { SERVICE_REQUEST_TYPES } from '@/constants/serviceRequests';

import { useRegions } from '@/services/api/hooks/region';
import { useGetServiceRequestTypes } from '@/services/api/hooks/serviceRequests';
import { useBasicBetterBestOptions } from '@/services/catalogue/useBasicBetterBest';
import { FC, PropsWithChildren, useMemo, useState } from 'react';
export const ProductForm: FC<PropsWithChildren> = ({ children }) => {
  return <div className="mb-8 flex gap-5">{children}</div>;
};

export const ProductDescription = () => {
  return (
    <div className="border-primary-std h-fit min-h-[24rem] w-full rounded-t-lg border bg-white">
      <RichTextField id="description" />
    </div>
  );
};

export const ProductSidebar: FC<PropsWithChildren> = ({ children }) => {
  return <div className="flex w-[32rem] flex-col gap-6">{children}</div>;
};

export const BaseProductProperties = () => {
  const [categories] = useCategories();
  const [manufacturers] = useManufacturers();
  const bbbOptions = useBasicBetterBestOptions();

  const [regions] = useRegions();

  const regionsOptions = useMemo(() => {
    return regions.map(region => {
      return {
        id: region.id,
        name: region.code
      };
    });
  }, [regions]);

  const { setValue, watch, control } = useFormContext();

  const category = watch('category') as Property;
  const isRateCardItem = watch('isRateCardItem') as boolean;

  const [triggers] = useGetRateCardTriggers();
  const triggerOptions = useMemo(() => {
    return (triggers || []).map(t => ({ value: t, label: t }));
  }, [triggers]);

  const [serviceRequestTypes] = useGetServiceRequestTypes();
  const serviceRequestTypeOptions = useMemo(() => {
    return (serviceRequestTypes || []).map(t => ({ value: t, label: t }));
  }, [serviceRequestTypes]);

  const [products] = useProducts();
  const rateCardItems = useMemo(() => {
    return (products || []).filter(p => p.isRateCardItem).map(p => ({ value: p.id, label: p.name }));
  }, [products]);

  const isRegionDisabled = useMemo(() => {
    const region = watch('region');
    return Boolean(region);
  }, []);

  const isMainDevice = useMemo(() => {
    return category && categories.find(item => item.id === category.id)?.isMainDevice;
  }, [category]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between mb-4">
        <label className="text-lg font-medium">Product</label>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Is Rate Card Service</label>
          <ToggleField id="isRateCardItem" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <TextField className="col-span-2" id="name" label="Name" placeholder="Product name" />
        <TextField id="productId" label="Manufacturer SKU" placeholder="SKU001" />
        <TextField id="code" label="SKU" placeholder="SKU001" />
        <SelectField
          id="manufacturer"
          label="Manufacturer"
          placeholder="Manufacturer"
          options={manufacturers.map(manufacturer => ({ id: manufacturer.id, name: manufacturer.name }))}
        />
        <SelectField
          id="category"
          label="Category"
          placeholder="Category"
          options={categories.map(category => ({ id: category.id, name: category.name }))}
        />
        <SelectField
          className="col-span-2"
          id="basicBetterBest"
          label="Basic / Better / Best"
          placeholder="Basic / Better / Best"
          options={bbbOptions}
        />
        <SelectField
          className="col-span-2"
          id="region"
          label="Region"
          placeholder="Region"
          options={regionsOptions}
          disabled={isRegionDisabled}
        />
        <GalleryField
          className="col-span-2"
          id="imageUri"
          label="Image"
          placeholder="Drop or click to upload product image"
        />
        <MultiTextAndIconField
          icons={SpecIcons}
          className="col-span-2"
          id="propertyBag"
          label="Product specifications"
          placeholder="Type to add"
        />
      </div>
      {isRateCardItem && (
        <>
          <div className="mt-6 flex flex-col gap-4 border-t pt-4">
            <label className="text-lg font-medium">Service & Inference Rules</label>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">Associated Device Types</label>
                <Controller
                  control={control}
                  name="triggerTypes"
                  render={({ field: { onChange, value } }) => (
                    <CreatableSelect
                      isMulti
                      options={triggerOptions}
                      value={(value || []).map((v: string) => triggerOptions.find(o => o.value === v) || { value: v, label: v })}
                      onChange={vals => onChange(vals ? vals.map((v: any) => v.value) : [])}
                      onCreateOption={val => {
                        if (window.confirm(`Are you sure you want to create a new trigger tag: '${val}'?`)) {
                          onChange([...(value || []), val]);
                        }
                      }}
                      placeholder="Select or create triggers..."
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">Applies to Service Requests</label>
                <Controller
                  control={control}
                  name="applicableRequestTypes"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      isMulti
                      options={serviceRequestTypeOptions}
                      value={(value || []).map((v: string) => ({ value: v, label: v }))}
                      onChange={vals => onChange(vals ? vals.map((v: any) => v.value) : [])}
                      placeholder="Select request types..."
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">Base Pack Request Type</label>
                <Controller
                  control={control}
                  name="basePackForRequestType"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      options={SERVICE_REQUEST_TYPES}
                      value={{ value: value || 'None', label: value || 'None' }}
                      onChange={val => onChange(val?.value)}
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">Per-Item Rate For Request Type</label>
                <Controller
                  control={control}
                  name="itemRateForRequestType"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      options={SERVICE_REQUEST_TYPES}
                      value={{ value: value || 'None', label: value || 'None' }}
                      onChange={val => onChange(val?.value)}
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>
              <div className="flex flex-col gap-1 mt-2">
                <TextField id="maxChargeableWeightKg" label="Max Chargeable Weight (kg)" onlyDigits={true} />
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-4 border-t pt-4">
            <label className="text-lg font-medium">Add-On Configuration</label>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">Add-On Category</label>
                <Controller
                  control={control}
                  name="addonCategory"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      options={[
                        { value: 'None', label: 'None' },
                        { value: 'Loaner', label: 'Loaner' },
                        { value: 'ReturnProcessing', label: 'Return Processing' }
                      ]}
                      value={{ value: value || 'None', label: value || 'None' }}
                      onChange={val => onChange(val?.value)}
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>
              {watch('addonCategory') && watch('addonCategory') !== 'None' && (
                <div className="flex flex-col gap-1">
                  <CheckboxField id="isCoveredByWarranty" label="Covered by ADR Warranty?" />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
